import type { GetRequestsListItem } from "@nlc/shared-types";
import type { SortDescriptor } from "react-stately";

import { TEAM_REQUESTS_STATUS_OPTIONS } from "@nlc/shared-types";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { MultiSelectDropdown } from "@/components/filters/MultiSelectDropdown";
import { Loader } from "@/components/Loader";
import { teamRequestsTableConfig } from "@/features/requests/config/tables/teamlead";
import { useTeamLeadTeamRequests } from "@/features/requests/hooks/teamlead";

import { LazyConfigurableTable } from "../../tables/LazyConfigurableTable";

function TeamRequestsTabComponent({ userId }: { userId?: string }) {
  const [selectedDesignerIds, setSelectedDesignerIds] = useState<number[]>([]);
  const [selectedStatusIds, setSelectedStatusIds] = useState<number[]>([]);
  const [designerPagination, setDesignerPagination] = useState<
    Map<number, { limit: number; page: number }>
  >(new Map());
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "createdAt",
    direction: "descending",
  });

  useEffect(() => {
    setSelectedDesignerIds([]);
    setSelectedStatusIds([]);
  }, []);

  const { data, isLoading } = useTeamLeadTeamRequests({
    designerIds: selectedDesignerIds,
    direction: sortDescriptor.direction === "ascending" ? "asc" : "desc",
    sortBy: sortDescriptor.column as string,
    statusIds: selectedStatusIds,
    userId,
  });

  const designers = useMemo(() => {
    if (data?.designers) {
      return data.designers;
    }

    if (!data?.requests) return [];

    const uniqueDesigners = new Map<number, { id: number; name: string }>();

    for (const request of data.requests) {
      if (request.assignee) {
        uniqueDesigners.set(request.assignee.id, {
          id: request.assignee.id,
          name: request.assignee.name,
        });
      }
    }

    return [...uniqueDesigners.values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [data?.designers, data?.requests]);

  const designerOptions = useMemo(
    () => designers.map((d) => ({ id: d.id, label: d.name })),
    [designers],
  );

  const allDesignersSelected = selectedDesignerIds.length === 0;
  const defaultStatusesSelected = selectedStatusIds.length === 0;
  const showDesignerIndicator = !allDesignersSelected;
  const showStatusIndicator = selectedStatusIds.length > 0;
  const showClearFilters = showDesignerIndicator || !defaultStatusesSelected;

  const requestsByDesigner = useMemo(() => {
    if (!data?.requests) return new Map();

    const grouped = new Map<number, GetRequestsListItem[]>();

    for (const request of data.requests) {
      if (request.assignee) {
        const designerId = request.assignee.id;

        if (!grouped.has(designerId)) {
          grouped.set(designerId, []);
        }

        grouped.get(designerId)!.push(request);
      }
    }

    return grouped;
  }, [data?.requests]);

  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor);
  }, []);

  const handleDesignerFilterChange = useCallback((ids: (number | string)[]) => {
    setSelectedDesignerIds(ids as number[]);
  }, []);

  const handleStatusFilterChange = useCallback((ids: (number | string)[]) => {
    setSelectedStatusIds(ids as number[]);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedDesignerIds([]);
    setSelectedStatusIds([]);
  }, []);

  const getDesignerPagination = useCallback(
    (designerId: number) =>
      designerPagination.get(designerId) || { limit: 10, page: 1 },
    [designerPagination],
  );

  const handleDesignerPageChange = useCallback(
    (designerId: number, newPage: number) => {
      setDesignerPagination((prev) => {
        const newMap = new Map(prev);
        const current = prev.get(designerId) || { limit: 10, page: 1 };

        newMap.set(designerId, { ...current, page: newPage });

        return newMap;
      });
    },
    [],
  );

  const handleDesignerItemsPerPageChange = useCallback(
    (designerId: number, newLimit: number) => {
      setDesignerPagination((prev) => {
        const newMap = new Map(prev);

        newMap.set(designerId, { limit: newLimit, page: 1 });

        return newMap;
      });
    },
    [],
  );

  const displayedDesigners =
    selectedDesignerIds.length > 0
      ? designers.filter((d) => selectedDesignerIds.includes(d.id))
      : designers;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 p-3 bg-bg-primary border-b border-border-secondary sticky top-0 z-10 lg:flex-row lg:flex-wrap lg:items-center">
        <MultiSelectDropdown
          defaultLabel="Team members"
          onChange={handleDesignerFilterChange}
          options={designerOptions}
          selectedValues={selectedDesignerIds}
          showSelectedIndicator={showDesignerIndicator}
        />
        <MultiSelectDropdown
          defaultLabel="Status"
          onChange={handleStatusFilterChange}
          options={TEAM_REQUESTS_STATUS_OPTIONS}
          selectedValues={selectedStatusIds}
          showSelectedIndicator={showStatusIndicator}
        />
        {showClearFilters && (
          <button
            className="flex items-center gap-2 px-3 py-2 font-semibold hover:cursor-pointer text-text-secondary hover:text-text-primary transition-colors"
            onClick={handleClearFilters}
            type="button"
          >
            Clear filters
            <svg
              aria-hidden="true"
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                d="M6 18L18 6M6 6l12 12"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
      <div className="space-y-8 pb-4">
        {data?.truncated && (
          <p className="px-4 text-sm text-text-secondary">
            Showing the first {data.requests.length} of {data.total} team
            requests.
          </p>
        )}
        {isLoading ? (
          <Loader className="h-64" />
        ) : displayedDesigners.length === 0 ? (
          <div className="text-center py-12 text-text-secondary">
            No team members found with active requests.
          </div>
        ) : (
          displayedDesigners.map((designer) => {
            const allDesignerRequests =
              requestsByDesigner.get(designer.id) || [];
            const pagination = getDesignerPagination(designer.id);

            const startIndex = (pagination.page - 1) * pagination.limit;
            const endIndex = startIndex + pagination.limit;
            const paginatedRequests = allDesignerRequests.slice(
              startIndex,
              endIndex,
            );
            const totalPages = Math.ceil(
              allDesignerRequests.length / pagination.limit,
            );

            return (
              <div className="space-y-2" key={designer.id}>
                <h3 className="text-lg font-semibold text-text-primary px-4">
                  {designer.name}
                </h3>
                {allDesignerRequests.length === 0 ? (
                  <div className="text-center py-8 text-text-secondary bg-bg-secondary rounded-md">
                    There&apos;s nothing to show at the moment
                  </div>
                ) : (
                  <LazyConfigurableTable
                    config={{
                      ...teamRequestsTableConfig,
                      id: `team-requests-${designer.id}`,
                      title: undefined,
                    }}
                    count={allDesignerRequests.length}
                    data={paginatedRequests}
                    isCountLoading={false}
                    isLoading={false}
                    paginationProps={{
                      metadata: {
                        currentPage: pagination.page,
                        itemsPerPage: pagination.limit,
                        totalItems: allDesignerRequests.length,
                        totalPages,
                      },
                      onItemsPerPageChange: (newLimit: number) =>
                        handleDesignerItemsPerPageChange(designer.id, newLimit),
                      onPageChange: (newPage: number) =>
                        handleDesignerPageChange(designer.id, newPage),
                      pageSizeOptions: [10, 25, 50, 100],
                      showPageSizeSelector: true,
                    }}
                    setSortDescriptor={handleSortChange}
                    sortDescriptor={sortDescriptor}
                    useAdvancedSorting={true}
                  />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export const TeamRequestsTab = memo(TeamRequestsTabComponent);
